import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { catchError, Observable, of, switchMap, take, tap } from 'rxjs';

import { MedicalCase } from '../../../domain/medical-case/model/medical-case';
import { MedicalCasePageCriteria } from '../../../domain/medical-case/model/medical-case-page-criteria';
import { MedicalCaseApiService } from '../../../domain/medical-case/service/medical-case-api.service';
import { MedicalCasePageItem } from '../../../domain/medical-case/model/medical-case-page-item';
import { Page } from '../../../shared/model/local/pagination/page';
import {
  CacheDataState,
  CacheDataStateModel,
} from '../../../shared/store/cache-data/cache-data.state';
import { CacheDataActions } from '../../../shared/store/cache-data/cache-data.actions';
import { isDataCached } from '../../../shared/helper/is-data-cached';
import { MedicalCaseActions } from './medical-case.actions';

export interface MedicalCaseStateModel {
  entities: Record<string, MedicalCase>;
}

const cacheKey = 'medical-case-list';

@State<MedicalCaseStateModel>({
  name: 'medicalCase',
  defaults: {
    entities: {},
  },
})
@Injectable()
export class MedicalCaseState {
  private readonly store = inject(Store);
  private readonly apiService = inject(MedicalCaseApiService);

  @Selector([CacheDataState])
  static page(state: CacheDataStateModel): Page<MedicalCasePageItem> | null {
    return CacheDataState.getDataByKey(cacheKey)(
      state
    ) as Page<MedicalCasePageItem> | null;
  }

  @Selector([CacheDataState])
  static criteria(state: CacheDataStateModel): MedicalCasePageCriteria | null {
    return CacheDataState.getCriteriaByKey(cacheKey)(
      state
    ) as MedicalCasePageCriteria | null;
  }

  @Selector()
  static entity(state: MedicalCaseStateModel) {
    return (id: string): MedicalCase | null => state.entities?.[id] ?? null;
  }

  @Action(MedicalCaseActions.GetPage, { cancelUncompleted: true })
  getPage(
    ctx: StateContext<MedicalCaseStateModel>,
    { criteria }: MedicalCaseActions.GetPage
  ): Observable<unknown> {
    console.debug('[medical-case][state] GetPage called', {
      criteria,
    });
    if (isDataCached(cacheKey, criteria, 1000 * 5)) {
      return this.fetchPageData(ctx, criteria);
    }
    return of(null);
  }

  @Action(MedicalCaseActions.RefreshPage, { cancelUncompleted: true })
  refreshPage(ctx: StateContext<MedicalCaseStateModel>): Observable<unknown> {
    return this.fetchPageData(ctx);
  }

  @Action(MedicalCaseActions.InvalidateCache)
  invalidateCache(): Observable<unknown> {
    return this.store.dispatch(new CacheDataActions.InvalidateCache(cacheKey));
  }

  @Action(MedicalCaseActions.GetById)
  getById(
    ctx: StateContext<MedicalCaseStateModel>,
    { id }: MedicalCaseActions.GetById
  ): Observable<unknown> {
    return this.fetchEntity(ctx, id, false);
  }

  @Action(MedicalCaseActions.GetByIdSilent)
  getByIdSilent(
    ctx: StateContext<MedicalCaseStateModel>,
    { id }: MedicalCaseActions.GetByIdSilent
  ): Observable<unknown> {
    return this.fetchEntity(ctx, id, true);
  }

  private fetchEntity(
    ctx: StateContext<MedicalCaseStateModel>,
    id: string,
    silentMode: boolean
  ): Observable<unknown> {
    const request$ = silentMode
      ? this.apiService.getByIdSilent(id)
      : this.apiService.getById(id);

    return request$.pipe(
      tap(entity => {
        ctx.patchState({
          entities: {
            ...ctx.getState().entities,
            [id]: entity,
          },
        });
      }),
      catchError(() => of(null))
    );
  }

  private fetchPageData(
    ctx: StateContext<MedicalCaseStateModel>,
    criteria?: MedicalCasePageCriteria
  ): Observable<unknown> {
    const pageCriteria$ = criteria
      ? of(criteria)
      : this.store.select(MedicalCaseState.criteria).pipe(take(1));

    return pageCriteria$.pipe(
      switchMap(criteriaFromStore => {
        let resolvedCriteria =
          criteria ?? criteriaFromStore ?? new MedicalCasePageCriteria();
        // Clean null values from criteria - new backend rejects null params
        resolvedCriteria = this.cleanNullValues(resolvedCriteria);
        console.debug('[medical-case][state] fetchPageData resolved criteria', {
          inputCriteria: criteria,
          criteriaFromStore,
          resolvedCriteria,
        });
        return this.apiService
          .getPage(resolvedCriteria)
          .pipe(
            switchMap(page =>
              this.store.dispatch(
                new CacheDataActions.SetCachedData(
                  cacheKey,
                  page,
                  resolvedCriteria
                )
              )
            )
          );
      })
    );
  }

  private cleanNullValues(
    criteria: MedicalCasePageCriteria
  ): MedicalCasePageCriteria {
    const cleaned = new MedicalCasePageCriteria();
    cleaned.pageNumber = criteria.pageNumber ?? 0;
    cleaned.pageSize = criteria.pageSize ?? 10;
    cleaned.sortOrders = criteria.sortOrders ?? undefined;
    cleaned.searchPhrase = criteria.searchPhrase || undefined;
    cleaned.excludedIds = criteria.excludedIds ?? undefined;
    cleaned.includedIds = criteria.includedIds ?? undefined;
    // archivedEq is handled in API service - always sends false
    console.debug('[medical-case][state] cleanNullValues', {
      input: criteria,
      output: cleaned,
    });
    return cleaned;
  }
}
